import * as R from 'ramda';
import { formatDate } from './itinerary-mining-utils';

const DEFAULT_PURCHASE_DATE_WINDOW_DAYS = 30;
const LIMIT_CAP = 10000;
const DEFAULT_TRAVEL_DATE_WINDOW_DAYS = 30;
export const MAX_MINING_YEARS = 2;

/** Matches filematch itinerary mining; override via REACT_APP_EARLIEST_MINING_YEAR if needed. */
const _earliestYear = parseInt(process.env.REACT_APP_EARLIEST_MINING_YEAR || '2016', 10);
export const EARLIEST_MINING_YEAR = Number.isFinite(_earliestYear) ? _earliestYear : 2016;

/** Default purchase window prefilled in the UI: today − 30 days through today. */
const computeDefaultPurchaseDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - DEFAULT_PURCHASE_DATE_WINDOW_DAYS);
  return {
    start: formatDate(start),
    end: formatDate(end),
  };
};

/** Default travel window prefilled in the UI: today − 30 through today + 30. */
export const computeDefaultTravelDateRange = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - DEFAULT_TRAVEL_DATE_WINDOW_DAYS);
  const end = new Date(today);
  end.setDate(end.getDate() + DEFAULT_TRAVEL_DATE_WINDOW_DAYS);
  return {
    start: formatDate(start),
    end: formatDate(end),
  };
};

const baseDefaultItineraryMining = {
  enabled: false,
  cron: '',
  lastJobMeta: null,
  includeQuotes: false,
  includeBookings: true,
  quotesLimit: '100',
  bookingsLimit: '100',
  /** User toggle; persisted config overrides. Default unchecked until explicitly enabled. */
  usePurchaseDateRange: false,
  useSelectedAgentId: false,
  nxAgentFilterEnabled: false,
  nxAgentIds: [],
  bookingReferenceIds: [],
};

const buildDefaultDraft = (overrides = {}) => {
  const purchaseDateRangeOverride = overrides.purchaseDateRange || {};
  const travelDateRangeOverride = overrides.travelDateRange || {};
  return {
    ...baseDefaultItineraryMining,
    ...overrides,
    purchaseDateRange: {
      ...computeDefaultPurchaseDateRange(),
      ...purchaseDateRangeOverride,
    },
    travelDateRange: {
      ...computeDefaultTravelDateRange(),
      ...travelDateRangeOverride,
    },
  };
};

export const defaultItineraryMining = buildDefaultDraft();

const clampLimit = value => {
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.min(num, LIMIT_CAP);
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const normalizePurchaseDateRange = (draftRange = {}) => {
  const fallbackRange = computeDefaultPurchaseDateRange();
  const start = hasOwn(draftRange, 'start') ? (draftRange.start || '') : fallbackRange.start;
  const end = hasOwn(draftRange, 'end') ? (draftRange.end || '') : fallbackRange.end;
  return { start, end };
};

const normalizeTravelDateRange = (draftRange = {}) => {
  const fallbackRange = computeDefaultTravelDateRange();
  const start = hasOwn(draftRange, 'start') ? (draftRange.start || '') : fallbackRange.start;
  const end = hasOwn(draftRange, 'end') ? (draftRange.end || '') : fallbackRange.end;
  return { start, end };
};

const normalizeBookingRefList = value => {
  if (value == null) return [];
  const arr = Array.isArray(value) ? value : String(value).split(/[\n,]+/);
  return arr.map(s => String(s).trim()).filter(Boolean);
};

const normalizeDraftForDirtyCompare = draft => ({
  ...draft,
  bookingReferenceIds: normalizeBookingRefList(draft.bookingReferenceIds),
});

export const normalizeItineraryDraftForPersistence = (draft = buildDefaultDraft()) => {
  const includeQuotes = Boolean(draft.includeQuotes);
  const includeBookings = Boolean(draft.includeBookings);
  const purchaseDateRange = normalizePurchaseDateRange(draft.purchaseDateRange);
  const travelDateRange = normalizeTravelDateRange(draft.travelDateRange);
  const nxAgentIds = Array.isArray(draft.nxAgentIds)
    ? draft.nxAgentIds.map(id => String(id).trim()).filter(Boolean)
    : [];
  const bookingReferenceIds = normalizeBookingRefList(draft.bookingReferenceIds);
  return {
    enabled: Boolean(draft.enabled),
    cron: draft.cron || '',
    lastJobMeta: draft.lastJobMeta || null,
    includeQuotes,
    includeBookings,
    quotesLimit: includeQuotes ? clampLimit(draft.quotesLimit) : null,
    bookingsLimit: includeBookings ? clampLimit(draft.bookingsLimit) : null,
    usePurchaseDateRange: Boolean(draft.usePurchaseDateRange),
    useSelectedAgentId: Boolean(draft.useSelectedAgentId),
    purchaseDateRange,
    travelDateRange,
    nxAgentFilterEnabled: nxAgentIds.length > 0,
    nxAgentIds,
    bookingReferenceIds,
  };
};

const initialJobState = {
  jobId: null,
  status: null,
  log: [],
  lastUpdated: null,
  isPolling: false,
  cooldownUntil: null,
};

const normalizeLogs = log => {
  if (!log) return [];
  if (Array.isArray(log)) return log;
  if (typeof log === 'string') return log.split('\n').filter(Boolean);
  return [];
};

const getErrorMessage = (payload, fallback) => {
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (payload && typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  if (payload && payload.json && typeof payload.json.message === 'string' && payload.json.message.trim()) return payload.json.message;
  return fallback;
};

const initialLastEnrichmentQueryState = {
  query: null,
  savedAt: null,
  key: null,
  loading: false,
  error: null,
  loadedAt: null,
};

const initialState = {
  configuration: null,
  draft: buildDefaultDraft(),
  loading: false,
  saving: false,
  clearing: false,
  dirty: false,
  error: null,
  lastSavedAt: null,
  job: initialJobState,
  statusLoading: false,
  affiliateAgents: [],
  affiliateAgentsLoading: false,
  affiliateAgentsError: null,
  lastEnrichmentQuery: initialLastEnrichmentQueryState,
};

const toLimitString = value => (value === null || value === undefined || Number.isNaN(value) ? '' : String(value));

const extractDraft = configuration => {
  if (!configuration) return buildDefaultDraft();
  const persisted = R.pathOr({}, ['itinerary_mining'], configuration);
  const base = buildDefaultDraft();
  const { dateRange: legacyDateRange, ...persistedWithoutDateRange } = persisted;
  const extracted = {
    ...base,
    ...persistedWithoutDateRange,
  };
  extracted.quotesLimit = toLimitString(persisted.quotesLimit ?? base.quotesLimit);
  extracted.bookingsLimit = toLimitString(persisted.bookingsLimit ?? base.bookingsLimit);
  const purchaseSource = persisted.purchaseDateRange || legacyDateRange; // dateRange is legacy field
  extracted.purchaseDateRange = normalizePurchaseDateRange(purchaseSource || base.purchaseDateRange);
  extracted.travelDateRange = normalizeTravelDateRange(persisted.travelDateRange || base.travelDateRange);

  extracted.nxAgentIds = Array.isArray(persisted.nxAgentIds) ? persisted.nxAgentIds : [];
  extracted.nxAgentFilterEnabled = extracted.nxAgentIds.length > 0;
  extracted.bookingReferenceIds = Array.isArray(persisted.bookingReferenceIds)
    ? persisted.bookingReferenceIds
    : normalizeBookingRefList(persisted.bookingReferenceIds);
  extracted.usePurchaseDateRange =
    typeof persisted.usePurchaseDateRange === 'boolean'
      ? persisted.usePurchaseDateRange
      : false;
  extracted.useSelectedAgentId =
    typeof persisted.useSelectedAgentId === 'boolean'
      ? persisted.useSelectedAgentId
      : false;
  // Never keep legacy API field on draft (purchaseDateRange is canonical).
  delete extracted.dateRange;
  return extracted;
};

export default function itineraryMining(state = initialState, { type, payload }) {
  switch (type) {
    case 'genAi_itineraryMining_reset':
      return {
        ...initialState,
        draft: buildDefaultDraft(),
      };
    case 'genAi_itineraryMining_loadAffiliateAgents_STARTED':
      return {
        ...state,
        affiliateAgentsLoading: true,
        affiliateAgentsError: null,
      };
    case 'genAi_itineraryMining_loadAffiliateAgents_SUCCESS':
      return {
        ...state,
        affiliateAgentsLoading: false,
        affiliateAgents: Array.isArray(payload) ? payload : [],
        affiliateAgentsError: null,
      };
    case 'genAi_itineraryMining_loadAffiliateAgents_ERROR':
      return {
        ...state,
        affiliateAgentsLoading: false,
        affiliateAgentsError: payload || 'Unable to load agents.',
      };
    case 'genAi_itineraryMining_loadConfig_STARTED':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'genAi_itineraryMining_loadConfig_SUCCESS': {
      const configuration = R.pathOr({}, [0, 'token', 'configuration'], payload) || {};
      return {
        ...state,
        loading: false,
        configuration,
        draft: extractDraft(configuration),
        dirty: false,
      };
    }
    case 'genAi_itineraryMining_loadConfig_ERROR':
      return {
        ...state,
        loading: false,
        error: payload,
      };
    case 'genAi_itineraryMining_updateDraft': {
      const nextDraft = {
        ...state.draft,
        ...payload,
      };
      const nxIds = Array.isArray(nextDraft.nxAgentIds) ? nextDraft.nxAgentIds : [];
      nextDraft.nxAgentFilterEnabled = nxIds.length > 0;
      return {
        ...state,
        draft: nextDraft,
        dirty: !R.equals(
          normalizeDraftForDirtyCompare(nextDraft),
          normalizeDraftForDirtyCompare(extractDraft(state.configuration)),
        ),
      };
    }
    case 'genAi_itineraryMining_saveConfig_STARTED':
      return {
        ...state,
        saving: true,
        error: null,
      };
    case 'genAi_itineraryMining_saveConfig_ERROR':
      return {
        ...state,
        saving: false,
        error: payload,
      };
    case 'genAi_itineraryMining_applySavedConfig': {
      const configuration = payload || state.configuration || {};
      return {
        ...state,
        saving: false,
        configuration,
        draft: extractDraft(configuration),
        dirty: false,
        lastSavedAt: Date.now(),
      };
    }
    case 'genAi_itineraryMining_runJob_STARTED':
      return {
        ...state,
        error: null,
        job: {
          ...initialJobState,
          status: 'starting',
        },
      };
    case 'genAi_itineraryMining_runJob_ERROR':
      return {
        ...state,
        job: initialJobState,
        error: payload?.message || payload || 'Unable to start itinerary matching.',
      };
    case 'genAi_itineraryMining_setJob':
      return {
        ...state,
        job: {
          ...state.job,
          ...payload,
        },
      };
    case 'genAi_itineraryMining_jobProgress': {
      if (!payload) return state;
      const existingLogs = state.job.log || [];
      const payloadLogs = normalizeLogs(payload.log);
      return {
        ...state,
        job: {
          ...state.job,
          jobId: payload.jobId || state.job.jobId,
          status: payload.status || state.job.status,
          log: payloadLogs.length ? payloadLogs : existingLogs,
          lastUpdated: Date.now(),
          isPolling: payload.isPolling ?? state.job.isPolling,
          cooldownUntil: payload.cooldownUntil ?? state.job.cooldownUntil,
        },
      };
    }
    case 'genAi_itineraryMining_pollingStarted':
      return {
        ...state,
        job: {
          ...state.job,
          isPolling: true,
        },
      };
    case 'genAi_itineraryMining_pollingStopped':
      return {
        ...state,
        job: {
          ...state.job,
          isPolling: false,
        },
      };
    case 'genAi_itineraryMining_clearJob':
      return {
        ...state,
        job: initialJobState,
      };
    case 'genAi_itineraryMining_loadJobStatus_STARTED':
      return {
        ...state,
        statusLoading: true,
      };
    case 'genAi_itineraryMining_loadJobStatus_SUCCESS': {
      const persisted = payload || null;
      if (!persisted) {
        return {
          ...state,
          statusLoading: false,
          job: initialJobState,
        };
      }
      return {
        ...state,
        statusLoading: false,
        job: {
          ...state.job,
          jobId: persisted.jobId,
          status: persisted.lastStatus,
          lastUpdated: persisted.lastUpdated || Date.now(),
          log: normalizeLogs(persisted.log).length ? normalizeLogs(persisted.log) : state.job.log,
          cooldownUntil: persisted.cooldownUntil ?? state.job.cooldownUntil,
        },
      };
    }
    case 'genAi_itineraryMining_loadJobStatus_ERROR':
      return {
        ...state,
        statusLoading: false,
      };
    case 'genAi_itineraryMining_cancelJob_STARTED':
      return {
        ...state,
        error: null,
      };
    case 'genAi_itineraryMining_cancelJob_ERROR':
      return {
        ...state,
        error: payload?.message || payload || 'Unable to cancel itinerary matching.',
      };
    case 'genAi_itineraryMining_clearDatabase_STARTED':
      return {
        ...state,
        clearing: true,
        error: null,
      };
    case 'genAi_itineraryMining_clearDatabase_SUCCESS': {
      const deletedKeys = Array.isArray(payload?.deletedKeys) ? payload.deletedKeys : [];
      const detailSuffix = deletedKeys.length ? ` Removed: ${deletedKeys.join(', ')}.` : '';
      const message = (payload?.message || 'Itinerary matching data cleared. Next run will rebuild it.') + detailSuffix;
      return {
        ...state,
        clearing: false,
        error: null,
        job: {
          ...initialJobState,
          status: payload?.status || 'cleared',
          log: [message],
          lastUpdated: Date.now(),
        },
        lastEnrichmentQuery: initialLastEnrichmentQueryState,
      };
    }
    case 'genAi_itineraryMining_loadLastQuery_STARTED':
      return {
        ...state,
        lastEnrichmentQuery: {
          ...state.lastEnrichmentQuery,
          loading: true,
          error: null,
        },
      };
    case 'genAi_itineraryMining_loadLastQuery_SUCCESS': {
      const queryPayload = payload || {};
      const query = queryPayload.query || null;
      return {
        ...state,
        lastEnrichmentQuery: {
          query,
          savedAt: queryPayload.savedAt || query?.savedAt || null,
          key: queryPayload.key || null,
          loading: false,
          error: null,
          loadedAt: Date.now(),
        },
      };
    }
    case 'genAi_itineraryMining_loadLastQuery_ERROR':
      return {
        ...state,
        lastEnrichmentQuery: {
          ...state.lastEnrichmentQuery,
          loading: false,
          error: getErrorMessage(payload, 'Unable to load last enrichment query.'),
        },
      };
    case 'genAi_itineraryMining_clearDatabase_ERROR':
      return {
        ...state,
        clearing: false,
        error: payload?.message || payload || 'Unable to clear itinerary matching data.',
      };
    default:
      return state;
  }
}
